/**
 * Offline Sync Manager
 * Handles queuing operations when offline and syncing when back online
 */

import { authService, supabase } from './supabase';
import { logger } from '../utils/logger';

const SYNC_QUEUE_KEY = 'repconnect_sync_queue';
const SYNC_STATUS_KEY = 'repconnect_sync_status';

class OfflineSyncManager {
  constructor() {
    this.isOnline = navigator.onLine;
    this.syncInProgress = false;
    this.setupNetworkListeners();
  }

  setupNetworkListeners() {
    // Listen for network status changes
    window.addEventListener('online', () => {
      logger.debug('🌐 Network connection restored');
      this.isOnline = true;
      this.processSyncQueue();
    });

    window.addEventListener('offline', () => {
      logger.debug('📡 Network connection lost - switching to offline mode');
      this.isOnline = false;
    });
  }

  /**
   * Add an operation to the sync queue
   */
  addToSyncQueue(operation) {
    logger.debug('📝 Adding operation to sync queue:', operation.type);
    
    const queue = this.getSyncQueue();
    const queueItem = {
      id: `sync_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date().toISOString(),
      ...operation
    };
    
    queue.push(queueItem);
    localStorage.setItem(SYNC_QUEUE_KEY, JSON.stringify(queue));
    
    // Try to sync immediately if online
    if (this.isOnline && !this.syncInProgress) {
      this.processSyncQueue();
    }
    
    return queueItem.id;
  }

  /**
   * Get the current sync queue
   */
  getSyncQueue() {
    try {
      const queue = localStorage.getItem(SYNC_QUEUE_KEY);
      return queue ? JSON.parse(queue) : [];
    } catch (error) {
      logger.error('Error reading sync queue', { error: error.message });
      return [];
    }
  }

  /**
   * Process all items in the sync queue
   */
  async processSyncQueue() {
    if (this.syncInProgress || !this.isOnline) {
      return;
    }

    const queue = this.getSyncQueue();
    if (queue.length === 0) {
      return;
    }

    logger.debug(`🔄 Processing ${queue.length} items in sync queue`);
    this.syncInProgress = true;

    const successfulSyncs = [];
    const failedSyncs = [];

    for (const item of queue) {
      try {
        logger.debug(`🔄 Syncing: ${item.type} (${item.id})`);
        const success = await this.syncItem(item);
        
        if (success) {
          successfulSyncs.push(item.id);
          logger.debug(`✅ Successfully synced: ${item.type} (${item.id})`);
        } else {
          failedSyncs.push(item);
          logger.debug(`❌ Failed to sync: ${item.type} (${item.id})`);
        }
      } catch (error) {
        logger.error('Error syncing item', { type: item.type, error: error.message });
        failedSyncs.push(item);
      }
    }

    // Remove successful syncs from queue
    const remainingQueue = queue.filter(item => !successfulSyncs.includes(item.id));
    localStorage.setItem(SYNC_QUEUE_KEY, JSON.stringify(remainingQueue));

    this.syncInProgress = false;

    // Update sync status
    this.updateSyncStatus({
      lastSync: new Date().toISOString(),
      totalSynced: successfulSyncs.length,
      totalFailed: failedSyncs.length,
      queueSize: remainingQueue.length
    });

    logger.debug(`🏁 Sync complete: ${successfulSyncs.length} success, ${failedSyncs.length} failed, ${remainingQueue.length} remaining`);
  }

  /**
   * Sync an individual item to Supabase
   */
  async syncItem(item) {
    try {
      switch (item.type) {
        case 'user_registration':
          return await this.syncUserRegistration(item);
        case 'profile_update':
          return await this.syncProfileUpdate(item);
        case 'onboarding_completion':
          return await this.syncOnboardingCompletion(item);
        default:
          logger.warn('Unknown sync type', { type: item.type });
          return false;
      }
    } catch (error) {
      logger.error('Sync error for item', { type: item.type, error: error.message });
      return false;
    }
  }

  /**
   * Sync user registration to Supabase
   */
  async syncUserRegistration(item) {
    const { email, password, userData } = item.data;
    
    try {
      // Check if user already exists in Supabase
      const { data: existingUser } = await supabase.auth.signInWithPassword({
        email,
        password
      });
      
      if (existingUser?.user) {
        logger.debug('User already exists in Supabase, skipping registration sync');
        return true;
      }
    } catch (error) {
      // User doesn't exist, proceed with registration
    }

    try {
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            first_name: userData.firstName,
            last_name: userData.lastName,
            user_type: userData.userType || 'customer'
          }
        }
      });

      if (authError) throw authError;

      if (authData.user) {
        const { data: profile, error: profileError } = await supabase
          .from('user_profiles')
          .insert([{
            id: authData.user.id,
            user_type: userData.userType || 'customer',
            first_name: userData.firstName || '',
            last_name: userData.lastName || '',
            email: email,
            phone: userData.phone || null,
            company: userData.company || null,
            territory: userData.territory || null
          }])
          .select()
          .single();

        if (profileError) {
          logger.error('Profile creation error during sync', { error: profileError.message });
        }

        return true;
      }
      
      return false;
    } catch (error) {
      logger.error('User registration sync failed', { error: error.message });
      return false;
    }
  }

  /**
   * Sync profile update to Supabase
   */
  async syncProfileUpdate(item) {
    const { userId, updates } = item.data;
    
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .update(updates)
        .eq('id', userId)
        .select()
        .single();

      if (error) throw error;
      return true;
    } catch (error) {
      logger.error('Profile update sync failed', { error: error.message });
      return false;
    }
  }

  /**
   * Sync onboarding completion to Supabase
   */
  async syncOnboardingCompletion(item) {
    const { userId, onboardingData } = item.data;
    
    try {
      const updates = {
        ...onboardingData,
        onboarding_completed: true,
        updated_at: new Date().toISOString()
      };

      const { data, error } = await supabase
        .from('user_profiles')
        .update(updates)
        .eq('id', userId)
        .select()
        .single();

      if (error) throw error;
      return true;
    } catch (error) {
      logger.error('Onboarding completion sync failed', { error: error.message });
      return false;
    }
  }

  /**
   * Update sync status in localStorage
   */
  updateSyncStatus(status) {
    const currentStatus = this.getSyncStatus();
    const updatedStatus = { ...currentStatus, ...status };
    localStorage.setItem(SYNC_STATUS_KEY, JSON.stringify(updatedStatus));
  }

  /**
   * Get current sync status
   */
  getSyncStatus() {
    try {
      const status = localStorage.getItem(SYNC_STATUS_KEY);
      return status ? JSON.parse(status) : {
        lastSync: null,
        totalSynced: 0,
        totalFailed: 0,
        queueSize: 0
      };
    } catch (error) {
      logger.error('Error reading sync status', { error: error.message });
      return {
        lastSync: null,
        totalSynced: 0,
        totalFailed: 0,
        queueSize: 0
      };
    }
  }

  /**
   * Manually trigger sync (useful for user-initiated sync)
   */
  async forcSync() {
    logger.debug('🔄 Force sync requested');
    await this.processSyncQueue();
  }

  /**
   * Clear the sync queue (useful for testing or reset)
   */
  clearSyncQueue() {
    localStorage.removeItem(SYNC_QUEUE_KEY);
    localStorage.removeItem(SYNC_STATUS_KEY);
    logger.debug('🗑️ Sync queue cleared');
  }

  /**
   * Get network status
   */
  getNetworkStatus() {
    return {
      isOnline: this.isOnline,
      queueSize: this.getSyncQueue().length,
      syncInProgress: this.syncInProgress,
      lastSync: this.getSyncStatus().lastSync
    };
  }
}

// Create singleton instance
const offlineSyncManager = new OfflineSyncManager();

export default offlineSyncManager;